import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { client } from "@/data/client";
import { cn, formatBRL } from "@/lib/utils";
import type { CloserMeetingOutcome, CloserStageEntry, CloserStageKey } from "@/data/types";

type Period = { periodStart: string; periodEnd: string };

const STAGE_CONFIG: Record<
  CloserStageKey,
  { title: string; subtitle: string; chip: string; accent: string }
> = {
  meetingsScheduled: {
    title: "Reuniões agendadas",
    subtitle: "Calendário confirmado · ordenado pela reunião mais próxima",
    chip: "bg-chart-4/15 text-chart-4",
    accent: "hsl(var(--chart-4))",
  },
  meetingsHeld: {
    title: "Reuniões realizadas",
    subtitle: "Closer executou a reunião · mais recentes primeiro",
    chip: "bg-chart-3/15 text-chart-3",
    accent: "hsl(var(--chart-3))",
  },
  sales: {
    title: "Vendas fechadas",
    subtitle: "Negócios com contrato assinado · mais recentes primeiro",
    chip: "bg-chart-1/15 text-chart-1",
    accent: "hsl(var(--chart-1))",
  },
  valueGenerated: {
    title: "Valor gerado",
    subtitle: "Mesmos negócios fechados · ordenados por ticket decrescente",
    chip: "bg-chart-2/15 text-chart-2",
    accent: "hsl(var(--chart-2))",
  },
};

const OUTCOME_LABEL: Record<CloserMeetingOutcome, string> = {
  "proposta-enviada": "Proposta enviada",
  "em-negociacao": "Em negociação",
  reagendou: "Reagendou",
  perdido: "Perdido",
};

const OUTCOME_CHIP: Record<CloserMeetingOutcome, string> = {
  "proposta-enviada": "bg-chart-4/15 text-chart-4",
  "em-negociacao": "bg-warning/15 text-warning",
  reagendou: "bg-chart-3/15 text-chart-3",
  perdido: "bg-danger/15 text-danger",
};

const PAGE_SIZE = 7;

export function CloserStageSection({
  stage,
  period,
  onClear,
}: {
  stage: CloserStageKey | null;
  period: Period;
  onClear: () => void;
}) {
  const [search, setSearch] = useState("");
  const [closerFilter, setCloserFilter] = useState<string>("all");
  const [page, setPage] = useState(1);

  const q = useQuery({
    queryKey: ["closer-stage", stage, period],
    queryFn: () => client.getCloserStageDetail({ stage: stage!, ...period }),
    enabled: !!stage,
  });

  useEffect(() => {
    setSearch("");
    setCloserFilter("all");
    setPage(1);
  }, [stage]);

  useEffect(() => {
    setPage(1);
  }, [search, closerFilter]);

  const detail = q.data;

  const closerOptions = useMemo(() => {
    const set = new Set<string>();
    (detail?.entries ?? []).forEach((e) => set.add(e.closerName));
    return ["all", ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, [detail]);

  const visible = useMemo(() => {
    if (!detail) return [];
    return detail.entries.filter((e) => {
      if (closerFilter !== "all" && e.closerName !== closerFilter) return false;
      if (search && !e.contactName.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [detail, closerFilter, search]);

  const totalPages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const pageItems = useMemo(
    () => visible.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [visible, page]
  );

  if (!stage) {
    return (
      <section className="flex h-full flex-col rounded-lg border border-dashed border-border-subtle bg-bg-card/50 p-8 text-center">
        <div className="m-auto max-w-md space-y-1.5">
          <div className="text-sm font-medium text-text-secondary">Detalhes do funil</div>
          <p className="text-[13px] text-text-muted">
            Clique em qualquer etapa do funil ao lado para listar os contatos —
            reuniões agendadas, realizadas, vendas fechadas ou valor gerado.
          </p>
        </div>
      </section>
    );
  }

  const cfg = STAGE_CONFIG[stage];

  return (
    <section className="flex h-full flex-col overflow-hidden rounded-lg border border-border-subtle bg-bg-card">
      <header className="flex items-start justify-between gap-4 border-b border-border-subtle px-6 py-5">
        <div className="space-y-1">
          <div
            className="text-[11px] font-medium uppercase tracking-[0.15em]"
            style={{ color: cfg.accent }}
          >
            Etapa do funil
          </div>
          <h2 className="text-lg font-semibold tracking-tight text-text-primary">{cfg.title}</h2>
          <p className="text-[13px] text-text-muted">{cfg.subtitle}</p>
          {detail && (
            <p className="text-[13px] text-text-secondary">
              <span className="font-semibold text-text-primary">{detail.total}</span>{" "}
              {stage === "sales" || stage === "valueGenerated" ? "vendas" : "contatos"} nesta etapa
              {detail.totalAmount !== undefined && (
                <>
                  {" · "}
                  <span className="font-semibold text-brand-amber">{formatBRL(detail.totalAmount)}</span>{" "}
                  em faturamento
                </>
              )}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onClear}
          className="flex items-center gap-1.5 rounded-md border border-border-subtle px-3 py-1.5 text-xs font-medium text-text-muted transition hover:border-border-strong hover:text-text-primary"
        >
          <span>×</span>
          <span>Fechar</span>
        </button>
      </header>

      <div className="flex flex-wrap items-center gap-3 border-b border-border-subtle px-6 py-3">
        <input
          type="search"
          placeholder="Buscar por nome do contato…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-9 flex-1 min-w-[220px] rounded-md border border-border-subtle bg-bg-primary px-3 text-sm text-text-primary placeholder:text-text-muted focus:border-brand-amber focus:outline-none"
        />
        <div className="flex items-center gap-2 text-xs text-text-muted">
          <span>Closer:</span>
          <select
            value={closerFilter}
            onChange={(e) => setCloserFilter(e.target.value)}
            className="h-9 rounded-md border border-border-subtle bg-bg-primary px-2 text-sm text-text-primary focus:border-brand-amber focus:outline-none"
          >
            {closerOptions.map((c) => (
              <option key={c} value={c}>{c === "all" ? "Todos" : c}</option>
            ))}
          </select>
        </div>
        <div className="ml-auto text-xs text-text-muted">
          {visible.length} de {detail?.total ?? 0}
        </div>
      </div>

      <div>
        {q.isLoading && (
          <div className="px-6 py-12 text-center text-sm text-text-muted">Carregando etapa…</div>
        )}
        {detail && visible.length === 0 && !q.isLoading && (
          <div className="px-6 py-12 text-center text-sm text-text-muted">
            Nenhum registro encontrado com os filtros atuais.
          </div>
        )}
        {visible.length > 0 && (
          <>
            <table className="w-full">
              <thead className="bg-bg-secondary">
                <tr className="text-[11px] font-medium uppercase tracking-[0.1em] text-text-muted">
                  <th className="px-5 py-3 text-left">Contato</th>
                  <th className="px-5 py-3 text-left">Closer</th>
                  <th className="px-5 py-3 text-left">{stageSpecificColumn(stage)}</th>
                  <th className="px-5 py-3 text-right">{stageSpecificAmountColumn(stage)}</th>
                  <th className="px-5 py-3 text-left">Observação</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((entry, i) => (
                  <EntryRow
                    key={entry.contactId}
                    entry={entry}
                    stage={stage}
                    chip={cfg.chip}
                    isLast={i === pageItems.length - 1}
                  />
                ))}
              </tbody>
            </table>
            {totalPages > 1 && (
              <Paginator
                page={page}
                totalPages={totalPages}
                onChange={setPage}
                rangeStart={(page - 1) * PAGE_SIZE + 1}
                rangeEnd={Math.min(page * PAGE_SIZE, visible.length)}
                total={visible.length}
              />
            )}
          </>
        )}
      </div>
    </section>
  );
}

function stageSpecificColumn(stage: CloserStageKey): string {
  switch (stage) {
    case "meetingsScheduled": return "Reunião marcada";
    case "meetingsHeld": return "Resultado";
    case "sales":
    case "valueGenerated": return "Produto";
  }
}

function stageSpecificAmountColumn(stage: CloserStageKey): string {
  if (stage === "sales" || stage === "valueGenerated") return "Valor";
  return "Dias parado";
}

function EntryRow({
  entry,
  stage,
  chip,
  isLast,
}: {
  entry: CloserStageEntry;
  stage: CloserStageKey;
  chip: string;
  isLast: boolean;
}) {
  const phoneFmt = entry.contactNumber.startsWith("55")
    ? `+55 (${entry.contactNumber.slice(2, 4)}) ${entry.contactNumber.slice(4, 9)}-${entry.contactNumber.slice(9)}`
    : entry.contactNumber;

  const specificCell = renderSpecific(entry, stage, chip);
  const rightCell = renderRight(entry, stage);

  return (
    <tr
      className={cn(
        "text-sm transition hover:bg-bg-card-hover",
        !isLast && "border-b border-border-subtle"
      )}
    >
      <td className="px-5 py-3">
        <div className="flex items-center gap-3">
          <span
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold text-text-on-accent"
            style={{ backgroundColor: entry.avatarColor }}
          >
            {getInitials(entry.contactName)}
          </span>
          <div className="min-w-0">
            <div className="font-medium text-text-primary">{entry.contactName}</div>
            <div className="text-xs text-text-muted">{phoneFmt}</div>
            <div className="text-[11px] text-text-muted">origem SDR · {entry.sdrOriginName}</div>
            {entry.tags.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {entry.tags.map((t) => (
                  <span
                    key={t.id}
                    className="inline-flex items-center gap-1 rounded-full bg-bg-primary px-1.5 py-0.5 text-[10px] font-medium text-text-secondary"
                  >
                    <span className="h-1 w-1 rounded-full" style={{ backgroundColor: t.color }} />
                    {t.name}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </td>
      <td className="px-5 py-3 text-text-secondary">{entry.closerName}</td>
      <td className="px-5 py-3">{specificCell}</td>
      <td className="px-5 py-3 text-right">{rightCell}</td>
      <td className="px-5 py-3 text-text-muted italic">{entry.lastNote ?? "—"}</td>
    </tr>
  );
}

function renderSpecific(entry: CloserStageEntry, stage: CloserStageKey, chip: string) {
  if (stage === "meetingsScheduled" && entry.scheduledMeetingAt) {
    const fmt = new Date(entry.scheduledMeetingAt).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
    return (
      <span className={cn("inline-flex rounded-full px-2 py-0.5 text-xs font-semibold", chip)}>
        {fmt}
      </span>
    );
  }
  if (stage === "meetingsHeld" && entry.meetingOutcome) {
    return (
      <span
        className={cn(
          "inline-flex rounded-full px-2 py-0.5 text-xs font-semibold",
          OUTCOME_CHIP[entry.meetingOutcome]
        )}
      >
        {OUTCOME_LABEL[entry.meetingOutcome]}
      </span>
    );
  }
  if (stage === "sales" || stage === "valueGenerated") {
    return <span className="text-text-secondary">{entry.productName ?? "—"}</span>;
  }
  return <span className="text-text-muted">—</span>;
}

function renderRight(entry: CloserStageEntry, stage: CloserStageKey) {
  if (stage === "sales" || stage === "valueGenerated") {
    return (
      <span className="font-semibold text-brand-amber">
        {entry.amount !== undefined ? formatBRL(entry.amount) : "—"}
      </span>
    );
  }
  const severity =
    entry.daysInStage >= 7
      ? "bg-danger/15 text-danger"
      : entry.daysInStage >= 3
        ? "bg-warning/15 text-warning"
        : "bg-success/15 text-success";
  return (
    <span className={cn("inline-flex rounded-full px-2 py-0.5 text-xs font-semibold", severity)}>
      {entry.daysInStage}d
    </span>
  );
}

function Paginator({
  page,
  totalPages,
  onChange,
  rangeStart,
  rangeEnd,
  total,
}: {
  page: number;
  totalPages: number;
  onChange: (p: number) => void;
  rangeStart: number;
  rangeEnd: number;
  total: number;
}) {
  return (
    <div className="flex items-center justify-between border-t border-border-subtle px-6 py-3">
      <span className="text-xs text-text-muted">
        Exibindo <span className="font-semibold text-text-primary">{rangeStart}–{rangeEnd}</span> de {total}
      </span>
      <div className="flex items-center gap-1">
        <PagerButton label="‹" disabled={page === 1} onClick={() => onChange(page - 1)} />
        {buildPageWindow(page, totalPages).map((p, i) =>
          p === "…" ? (
            <span key={`ellipsis-${i}`} className="px-2 text-xs text-text-muted">…</span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => onChange(p)}
              className={cn(
                "min-w-[30px] rounded-md px-2 py-1 text-xs font-medium transition",
                p === page
                  ? "bg-brand-amber text-text-on-accent"
                  : "text-text-secondary hover:bg-bg-card-hover hover:text-text-primary"
              )}
            >
              {p}
            </button>
          )
        )}
        <PagerButton label="›" disabled={page === totalPages} onClick={() => onChange(page + 1)} />
      </div>
    </div>
  );
}

function PagerButton({ label, disabled, onClick }: { label: string; disabled: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "flex h-7 w-7 items-center justify-center rounded-md text-sm transition",
        disabled
          ? "cursor-not-allowed text-text-muted/40"
          : "text-text-secondary hover:bg-bg-card-hover hover:text-text-primary"
      )}
    >
      {label}
    </button>
  );
}

function buildPageWindow(current: number, total: number): (number | "…")[] {
  if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | "…")[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) pages.push("…");
  for (let p = start; p <= end; p++) pages.push(p);
  if (end < total - 1) pages.push("…");
  pages.push(total);
  return pages;
}

function getInitials(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
